import type { QueryClient } from '@tanstack/react-query';

import { canvasSnapshotKeys, createCanvasSnapshotQueryFilter } from '@/hooks/api/useCanvasSnapshot';
import { getCanvasConversationHubNodeId } from '@/lib/canvas/canvas-reference-graph';
import { getCanvasNodeVisualCenter } from '@/lib/canvas/canvas-node-focus';
import { buildGenerationTargetCacheKey } from '@/lib/generation/generation-reference';
import type { CanvasConversationSummary, CanvasSnapshot } from '@/types/canvas-snapshot';
import type { GenerationTargetKey } from '@/types/generation-reference';

export type RevealConversationHubViewportApi = {
  getNode: (id: string) =>
    | {
        position: { x: number; y: number };
        measured?: { width?: number; height?: number };
        width?: number;
        height?: number;
        type?: string;
      }
    | undefined;
  setCenter: (
    x: number,
    y: number,
    options?: { zoom?: number; duration?: number }
  ) => void | Promise<boolean>;
  getZoom: () => number;
};

/** 合并会话 target：入站 planId/tagId 为空时保留已有值（视频 create 无 planId 的关键坑）。 */
export function mergeCanvasConversationTarget(
  incoming: GenerationTargetKey,
  existing?: GenerationTargetKey | null
): GenerationTargetKey {
  return {
    ...incoming,
    planId: incoming.planId ?? existing?.planId ?? null,
    tagId: incoming.tagId ?? existing?.tagId ?? null,
  };
}

export function buildOptimisticCanvasConversationSummary(params: {
  conversationId: string;
  target: GenerationTargetKey;
  title?: string | null;
  messageCount?: number;
  lastMessageAt?: string | null;
  userId?: string | null;
}): CanvasConversationSummary {
  return {
    conversationId: params.conversationId,
    userId: params.userId ?? null,
    target: params.target,
    title: params.title ?? null,
    messageCount: params.messageCount ?? 0,
    lastMessageAt: params.lastMessageAt ?? null,
    firstMessages: [],
  };
}

/** 会话枢纽按用户隔离：只有 actor 本人才能把增量写入自己的 snapshot。 */
export function isOwnCanvasConversationChange(params: {
  actorUserId?: string | null;
  conversationUserId?: string | null;
  currentUserId?: string | null;
}): boolean {
  const currentUserId = params.currentUserId?.trim() ?? '';
  if (!currentUserId) return false;
  const ownerId = (params.conversationUserId ?? params.actorUserId)?.trim() ?? '';
  return ownerId.length > 0 && ownerId === currentUserId;
}

export function isOwnedCanvasConversation(
  conversation: Pick<CanvasConversationSummary, 'userId'> | null | undefined,
  currentUserId: string | null | undefined
): boolean {
  if (!conversation) return false;
  const ownerId = conversation.userId?.trim() ?? '';
  if (!ownerId) return true;
  const current = currentUserId?.trim() ?? '';
  return current.length > 0 && ownerId === current;
}

export function selectOwnedCanvasConversations(
  conversations: readonly CanvasConversationSummary[],
  currentUserId: string | null | undefined
): CanvasConversationSummary[] {
  return conversations.filter((conversation) =>
    isOwnedCanvasConversation(conversation, currentUserId)
  );
}

export function isForbiddenConversationAccessError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as { code?: unknown; response?: { status?: unknown } };
  return candidate.code === 403 || candidate.response?.status === 403;
}

export function removeCanvasSnapshotConversationById(
  queryClient: QueryClient,
  params: {
    projectId: string;
    conversationId: string;
  }
): void {
  queryClient.setQueriesData<CanvasSnapshot>(createCanvasSnapshotQueryFilter(), (snapshot) => {
    if (!snapshot || snapshot.projectId !== params.projectId) return snapshot;
    const next = snapshot.conversations.filter(
      (item) => item.conversationId !== params.conversationId
    );
    if (next.length === snapshot.conversations.length) return snapshot;
    return { ...snapshot, conversations: next };
  });
}

export function upsertCanvasSnapshotConversation(
  queryClient: QueryClient,
  params: {
    projectId: string;
    conversation: CanvasConversationSummary;
  }
): void {
  queryClient.setQueriesData<CanvasSnapshot>(createCanvasSnapshotQueryFilter(), (snapshot) => {
    if (!snapshot || snapshot.projectId !== params.projectId) return snapshot;
    const existing = snapshot.conversations.find(
      (item) => item.conversationId === params.conversation.conversationId
    );
    const nextTarget = mergeCanvasConversationTarget(params.conversation.target, existing?.target);
    if (!existing) {
      return {
        ...snapshot,
        conversations: [
          ...snapshot.conversations,
          {
            ...params.conversation,
            target: nextTarget,
          },
        ],
      };
    }
    const messageById = new Map(
      [...existing.firstMessages, ...params.conversation.firstMessages].map((message) => [
        message.messageId,
        message,
      ])
    );
    return {
      ...snapshot,
      conversations: snapshot.conversations.map((item) =>
        item.conversationId === params.conversation.conversationId
          ? {
              ...item,
              ...params.conversation,
              target: nextTarget,
              firstMessages: Array.from(messageById.values())
                .sort(
                  (left, right) =>
                    new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
                )
                .slice(0, 8),
            }
          : item
      ),
    };
  });
}

/**
 * 删除图片方案 / 资产标签后，从 snapshot 乐观移除匹配会话，避免孤立 hub 残留。
 * - 图片方案：匹配 target.planId（snapshot 将 conversation.tag_id 映射为 planId）
 * - 资产标签：匹配 target.tagId
 */
export function removeCanvasSnapshotConversationsByPlanOrTag(
  queryClient: QueryClient,
  params: {
    projectId?: string;
    planId?: string | null;
    tagId?: string | null;
  }
): void {
  const planId = params.planId ?? null;
  const tagId = params.tagId ?? null;
  if (!planId && !tagId) return;

  queryClient.setQueriesData<CanvasSnapshot>(createCanvasSnapshotQueryFilter(), (snapshot) => {
    if (!snapshot) return snapshot;
    if (params.projectId && snapshot.projectId !== params.projectId) return snapshot;
    const next = snapshot.conversations.filter((conversation) => {
      if (planId && conversation.target.planId === planId) return false;
      if (tagId && conversation.target.tagId === tagId) return false;
      return true;
    });
    if (next.length === snapshot.conversations.length) return snapshot;
    return { ...snapshot, conversations: next };
  });
}

export function invalidateCanvasSnapshotQueries(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({
    queryKey: [...canvasSnapshotKeys.all, projectId],
  });
}

export function resolveConversationHubNodeId(target: GenerationTargetKey): string {
  return getCanvasConversationHubNodeId(buildGenerationTargetCacheKey(target));
}

/**
 * 仅视口居中到会话枢纽，**不**改 activeNodeId。
 * 节点尚未投影时返回 false，调用方可 pending 重试。
 */
export function revealConversationHubInViewport(
  api: RevealConversationHubViewportApi,
  hubNodeId: string,
  options?: {
    duration?: number;
    fallbackSize?: { width: number; height: number };
  }
): boolean {
  const node = api.getNode(hubNodeId);
  if (!node) return false;
  const center = getCanvasNodeVisualCenter(
    node,
    options?.fallbackSize ?? { width: 280, height: 200 }
  );
  void api.setCenter(center.x, center.y, {
    zoom: api.getZoom(),
    duration: options?.duration ?? 180,
  });
  return true;
}

/** 节点尚未出现时短重试 setCenter；绝不调用 focus / setActive。 */
export function scheduleRevealConversationHubInViewport(
  getApi: () => RevealConversationHubViewportApi | null,
  hubNodeId: string,
  options?: {
    duration?: number;
    maxAttempts?: number;
    intervalMs?: number;
  }
): void {
  const maxAttempts = options?.maxAttempts ?? 12;
  const intervalMs = options?.intervalMs ?? 50;
  let attempts = 0;

  const tick = () => {
    attempts += 1;
    const api = getApi();
    if (api && revealConversationHubInViewport(api, hubNodeId, { duration: options?.duration })) {
      return;
    }
    if (attempts >= maxAttempts) return;
    window.setTimeout(tick, intervalMs);
  };

  tick();
}
